import { useState, useEffect } from "react";
import { Plus, X, Edit, Trash2, BookOpen, UserPlus, ArrowUpCircle } from "lucide-react";
import { API, SPIERGROEPEN_LIB, APPARATEN, TYPES } from "./adminConstants";

export default function OefenbibliotheekTab({ token }) {
  const [exercises, setExercises] = useState([]);
  const [userExercises, setUserExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [form, setForm] = useState({ naam: "", spiergroep: "Borst", type: "", apparaat: "", uitvoeringstip: "" });
  const authHeaders = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const load = () => {
    fetch(`${API}/api/admin/exercises`, { headers: authHeaders })
      .then(r => r.json()).then(d => { setExercises(d.exercises || []); setLoading(false); });
    fetch(`${API}/api/admin/user-exercises`, { headers: authHeaders })
      .then(r => r.json()).then(d => setUserExercises(d.exercises || [])).catch(() => {});
  };
  useEffect(load, []);

  const promoveer = async (id) => {
    await fetch(`${API}/api/admin/user-exercises/${id}/promoveer`, { method: "POST", headers: authHeaders });
    load();
  };
  const verwijderUserEx = async (id) => {
    await fetch(`${API}/api/admin/user-exercises/${id}`, { method: "DELETE", headers: authHeaders });
    setUserExercises(prev => prev.filter(e => e.id !== id));
  };

  const openAdd = () => {
    setForm({ naam: "", spiergroep: "Borst", type: "", apparaat: "", uitvoeringstip: "" });
    setModal({ mode: "add" });
  };
  const openEdit = (ex) => {
    setForm({ naam: ex.naam, spiergroep: ex.spiergroep, type: ex.type || "", apparaat: ex.apparaat || "", uitvoeringstip: ex.uitvoeringstip || "" });
    setModal({ mode: "edit", id: ex.id });
  };

  const handleSave = async () => {
    if (!form.naam.trim()) return;
    setSaving(true);
    const url = modal.mode === "add" ? `${API}/api/admin/exercises` : `${API}/api/admin/exercises/${modal.id}`;
    const method = modal.mode === "add" ? "POST" : "PUT";
    await fetch(url, { method, headers: authHeaders, body: JSON.stringify(form) });
    setModal(null);
    setSaving(false);
    setLoading(true);
    load();
  };

  const handleDelete = async (id) => {
    await fetch(`${API}/api/admin/exercises/${id}`, { method: "DELETE", headers: authHeaders });
    setDeleteConfirm(null);
    setExercises(prev => prev.filter(e => e.id !== id));
  };

  const SPIER_COLORS = { Borst: "text-blue-400", Rug: "text-green-400", Schouders: "text-purple-400", Biceps: "text-orange-400", Triceps: "text-red-400", Benen: "text-yellow-400", Hamstrings: "text-yellow-500", Billen: "text-pink-400", Core: "text-cyan-400", Kuiten: "text-teal-400" };
  const filtered = filter ? exercises.filter(e => e.spiergroep === filter) : exercises;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-white font-bold text-lg flex items-center gap-2"><BookOpen size={18} className="text-[#C9A84C]" /> Oefeningen Bibliotheek</h2>
          <p className="text-zinc-500 text-xs mt-0.5">{exercises.length} eigen oefeningen · beschikbaar in Schema Maker & Bewerken</p>
        </div>
        <button onClick={openAdd} className="flex items-center gap-2 bg-[#C9A84C] text-black px-4 py-2 rounded-xl text-sm font-bold hover:bg-[#D4AF37] transition-colors">
          <Plus size={14} /> Oefening toevoegen
        </button>
      </div>

      <div className="flex gap-1.5 flex-wrap mb-5">
        <button onClick={() => setFilter("")} className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${!filter ? "bg-[#C9A84C] text-black" : "bg-[#111] border border-white/8 text-zinc-400 hover:text-white"}`}>Alles</button>
        {SPIERGROEPEN_LIB.map(s => (
          <button key={s} onClick={() => setFilter(filter === s ? "" : s)}
            className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${filter === s ? "bg-[#C9A84C] text-black" : "bg-[#111] border border-white/8 text-zinc-400 hover:text-white"}`}>{s}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-zinc-600 text-center py-12">Laden...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-white/8 rounded-2xl">
          <BookOpen size={28} className="text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">Nog geen eigen oefeningen{filter ? ` voor ${filter}` : ""}</p>
          <button onClick={openAdd} className="mt-4 text-[#C9A84C] text-xs hover:underline">+ Eerste oefening toevoegen</button>
        </div>
      ) : (
        <div className="grid gap-2">
          {filtered.map(ex => (
            <div key={ex.id} className="bg-[#111] border border-white/8 rounded-xl px-4 py-3 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <span className="text-white text-sm font-semibold block truncate">{ex.naam}</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-xs font-semibold ${SPIER_COLORS[ex.spiergroep] || "text-zinc-400"}`}>{ex.spiergroep}</span>
                  {ex.type && <span className="text-zinc-600 text-xs">· {ex.type}</span>}
                  {ex.apparaat && <span className="text-zinc-600 text-xs">· {ex.apparaat}</span>}
                </div>
                {ex.uitvoeringstip && <p className="text-zinc-600 text-xs mt-1 truncate">{ex.uitvoeringstip}</p>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => openEdit(ex)} className="p-2 text-zinc-500 hover:text-[#C9A84C] transition-colors"><Edit size={13} /></button>
                <button onClick={() => setDeleteConfirm(ex)} className="p-2 text-zinc-500 hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {userExercises.length > 0 && (
        <div className="mt-8" data-testid="sporter-oefeningen-sectie">
          <div className="mb-4">
            <h3 className="text-white font-bold text-base flex items-center gap-2">
              <UserPlus size={16} className="text-[#C9A84C]" /> Door sporters toegevoegd
            </h3>
            <p className="text-zinc-500 text-xs mt-0.5">{userExercises.length} oefeningen · alleen zichtbaar voor de sporter zelf. Promoveer om ze voor iedereen beschikbaar te maken.</p>
          </div>
          <div className="grid gap-2">
            {userExercises.map(ex => (
              <div key={ex.id} className="bg-[#0d0d0d] border border-dashed border-[#C9A84C]/25 rounded-xl px-4 py-3 flex items-center gap-4" data-testid={`sporter-oefening-${ex.id}`}>
                <div className="flex-1 min-w-0">
                  <span className="text-white text-sm font-semibold block truncate">{ex.naam}</span>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className={`text-xs font-semibold ${SPIER_COLORS[ex.spiergroep] || "text-zinc-400"}`}>{ex.spiergroep}</span>
                    {ex.apparaat && <span className="text-zinc-600 text-xs">· {ex.apparaat}</span>}
                    <span className="text-zinc-500 text-xs">· door {ex.sporter}</span>
                  </div>
                  {ex.uitvoeringstip && <p className="text-zinc-600 text-xs mt-1 truncate">{ex.uitvoeringstip}</p>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => promoveer(ex.id)}
                    data-testid={`promoveer-btn-${ex.id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-[#C9A84C]/10 text-[#C9A84C] border border-[#C9A84C]/25 hover:bg-[#C9A84C]/20 transition-colors">
                    <ArrowUpCircle size={12} /> Promoveren
                  </button>
                  <button onClick={() => verwijderUserEx(ex.id)} className="p-2 text-zinc-500 hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#111] border border-white/10 rounded-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/8">
              <h3 className="text-white font-bold">{modal.mode === "add" ? "Oefening toevoegen" : "Oefening bewerken"}</h3>
              <button onClick={() => setModal(null)} className="text-zinc-500 hover:text-white"><X size={16} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-zinc-500 text-xs font-bold uppercase tracking-widest block mb-1.5">Naam *</label>
                <input value={form.naam} onChange={e => setForm(f => ({ ...f, naam: e.target.value }))}
                  className="course-input w-full" placeholder="bijv. Squat – Barbell" autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-500 text-xs font-bold uppercase tracking-widest block mb-1.5">Spiergroep</label>
                  <select value={form.spiergroep} onChange={e => setForm(f => ({ ...f, spiergroep: e.target.value }))} className="course-input w-full">
                    {SPIERGROEPEN_LIB.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-500 text-xs font-bold uppercase tracking-widest block mb-1.5">Type</label>
                  <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="course-input w-full">
                    <option value="">— kies —</option>
                    {TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-zinc-500 text-xs font-bold uppercase tracking-widest block mb-1.5">Apparaat</label>
                <select value={form.apparaat} onChange={e => setForm(f => ({ ...f, apparaat: e.target.value }))} className="course-input w-full">
                  <option value="">— kies —</option>
                  {APPARATEN.map(a => <option key={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label className="text-zinc-500 text-xs font-bold uppercase tracking-widest block mb-1.5">Uitvoeringstip</label>
                <textarea value={form.uitvoeringstip} onChange={e => setForm(f => ({ ...f, uitvoeringstip: e.target.value }))}
                  rows={3} className="course-input w-full resize-none" placeholder="bijv. Hou je rug recht en knieën boven je voeten..." />
              </div>
            </div>
            <div className="px-6 pb-5 flex gap-3">
              <button onClick={() => setModal(null)} className="flex-1 py-2.5 rounded-xl border border-white/10 text-zinc-400 text-sm font-semibold hover:text-white transition-colors">Annuleren</button>
              <button onClick={handleSave} disabled={saving || !form.naam.trim()}
                className="flex-1 py-2.5 rounded-xl bg-[#C9A84C] text-black text-sm font-bold hover:bg-[#D4AF37] transition-colors disabled:opacity-50">
                {saving ? "Opslaan..." : modal.mode === "add" ? "Toevoegen" : "Opslaan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#111] border border-white/10 rounded-2xl p-6 max-w-sm w-full text-center">
            <Trash2 size={28} className="text-red-400 mx-auto mb-3" />
            <p className="text-white font-bold mb-1">Oefening verwijderen?</p>
            <p className="text-zinc-500 text-sm mb-5">"{deleteConfirm.naam}" wordt permanent verwijderd.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 rounded-xl border border-white/10 text-zinc-400 text-sm font-semibold hover:text-white">Annuleren</button>
              <button onClick={() => handleDelete(deleteConfirm.id)} className="flex-1 py-2.5 rounded-xl bg-red-500/20 text-red-400 text-sm font-bold hover:bg-red-500/30 border border-red-500/30">Verwijderen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
